import { Injectable } from "@angular/core";
import { DemoInstance, DemoSnapshot, InstanceRequest, PowerState, ProvisioningState, getPowerStatusTextDemoInstance } from "../core";
import { Observable, Subject, merge } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { map, scan } from "rxjs/operators";
import { PollingService } from "./poll.service";
import { brdCatchError } from "../shared/message-utils";
import { LimitService } from "./limit.service";

@Injectable({
  providedIn: "root"
})
export class DemoInstancesService {
  constructor(
    private polling: PollingService,
    private http: HttpClient,
    private limitService: LimitService
  ) {}

  controllerName = "DemoInstance";
  private apiUrl: string = `${environment.API}/${this.controllerName}`;

  private demoInsertSubject = new Subject<DemoInstance>();
  private demoUpdateSubject = new Subject<DemoInstance>();
  private demoRemovedSubject = new Subject<string>();
  private demosSubject = new Subject<DemoInstance[]>();

  demoInstances: DemoInstance[];
  demoInsert$ = this.demoInsertSubject.asObservable();
  demoUpdate$ = this.demoUpdateSubject.asObservable();
  demoRemoved$ = this.demoRemovedSubject.asObservable();
  demos$ = this.demosSubject.asObservable();

  homeDemos$ = this.demoWithActions(this.demos$).pipe(
    map(instances => {
      this.demoInstances = instances;
      return instances;
    })
  );

  adminDemos$ = this.demoWithActions(this.http.get<DemoInstance[]>(`${this.apiUrl}/datagrid`)).pipe(
    map(instances => {
      this.demoInstances = instances;
      return instances;
    })
  );

  getHomeDemos(group?: string): Observable<DemoInstance[]> {
    return this.demoWithActions(
      this.http
        .get<DemoInstance[]>(this.apiUrl, {
          params: group ? { group } : null
        })
        .pipe(
          map(instances => {
            this.demosSubject.next(instances);
            return instances;
          })
        )
    );
  }

  demoWithActions(demoInstances: Observable<DemoInstance[]>): Observable<DemoInstance[]> {
    return merge(demoInstances, this.demoInsert$, this.demoUpdate$, this.demoRemoved$).pipe(
      scan((acc, value) => {
        if (value instanceof Array) return [...value];
        if (typeof value === "string") return acc.filter(val => val.id !== value);

        const i = acc.findIndex(val => val.id === value.id);
        if (i > -1) {
          acc[i] = value;
          return [...acc];
        } else {
          return [...acc, value];
        }
      }, [] as DemoInstance[])
    );
  }

  deleteDemoInstance(id: string): Observable<DemoInstance> {
    return this.http.delete<DemoInstance>(`${this.apiUrl}/${id}`).pipe(
      brdCatchError(),
      map(res => {
        this.demoUpdateSubject.next(res);

        return res;
      })
    );
  }

  restartDemoInstanceById(id: string): Observable<DemoInstance> {
    const instance = this.demoInstances.find(i => i.id === id);

    return this.restartDemoInstance(instance);
  }

  restartDemoInstance(instance: DemoInstance): Observable<DemoInstance> {
    return this.http.post<DemoInstance>(`${this.apiUrl}/restart`, { id: instance.id }).pipe(
      brdCatchError(),
      map(res => {
        this.demoUpdateSubject.next(res);
        return res;
      })
    );
  }

  stopDemoInstance(id: string): Observable<DemoInstance> {
    const instance = this.demoInstances.find(i => i.id === id);

    return this.http.post<DemoInstance>(`${this.apiUrl}/stop`, { id: instance.id }).pipe(
      brdCatchError(),
      map(res => {
        this.demoUpdateSubject.next(res);
        return res;
      })
    );
  }

  addInstance(instance: InstanceRequest): Observable<DemoInstance> {
    return this.http.post<DemoInstance>(this.apiUrl, instance).pipe(
      brdCatchError(),
      map(demoInstance => {
        this.demoInsertSubject.next(demoInstance);
        this.limitService.increaseCurrentUsage();

        return demoInstance;
      })
    );
  }

  getInstances(): Observable<DemoInstance[]> {
    return this.http.get<DemoInstance[]>(this.apiUrl);
  }

  getInstanceAvailability(id: string, name: string, regionId: string, powerState: string, provisioningState: string): Observable<boolean> {
    const params = {
      id,
      name,
      regionId,
      powerState,
      provisioningState
    };
    return this.http.get<boolean>(`${this.apiUrl}/getInstanceAvailability`, { params });
  }

  getInstanceByVmIdentifier(id: string): Observable<DemoInstance | null> {
    return this.http.get<DemoInstance>(this.apiUrl + "/getDetails/", { params: { id } }).pipe(
      brdCatchError<DemoInstance | null>(err => {
        console.warn(err);
        return null;
      })
    );
  }

  mapDemoInstance(demo: DemoInstance, user: string, isSuperUser: boolean): DemoInstance {
    const powerStateText = getPowerStatusTextDemoInstance(demo.powerState, demo.provisioningState, demo.available);
    switch (powerStateText) {
      case "Sleep Mode":
        this.startPollingStopInstance(demo.id);
        break;
      case "Deleting":
        this.startPollingDeleteInstance(demo.id);
        break;
      case "Creating":
        this.startPollingCreateInstance(demo.id);
        break;
      case "Starting":
        this.startPollingCreateInstance(demo.id, true);
        break;
      default:
        break;
    }

    return { ...demo, powerStateText, canDelete: demo.owner === user || isSuperUser };
  }

  updateNote(instanceId: string, comment: string): Observable<DemoInstance> {
    return this.http.patch<DemoInstance>(`${this.apiUrl}/note`, { instanceId, comment }).pipe(
      map(res => {
        this.demoUpdateSubject.next(res);
        return res;
      })
    );
  }

  addOrUpdateSnapshot(demoInstance: DemoInstance): Observable<DemoSnapshot> {
    return this.http.post<DemoSnapshot>(`${this.apiUrl}/snapshot`, { instanceId: demoInstance.id }).pipe(
      map(res => {
        const instanceUpdated = { ...demoInstance };
        instanceUpdated.snapshot = res;
        this.demoUpdateSubject.next(instanceUpdated);

        return res;
      })
    );
  }

  private startPollingStopInstance(id: string): void {
    this.getInstanceByVmIdentifier(id)
      .pipe(
        this.polling.pollUntill(45000, 45000, 40, res => {
          const check = res !== null && res.powerState === PowerState.Offline && res.provisioningState === ProvisioningState.Completed;
          if (check) this.demoUpdateSubject.next(res);
          return !check;
        })
      )
      .subscribe();
  }

  private startPollingDeleteInstance(id: string): void {
    this.getInstanceByVmIdentifier(id)
      .pipe(
        this.polling.pollUntill(45000, 45000, 40, (res: DemoInstance | null) => {
          const check = res === null || res.powerState === PowerState.Deleted;
          if (check) {
            this.demoRemovedSubject.next(id);
            this.limitService.decreaseCurrentUsage();
          }
          return !check;
        })
      )
      .subscribe();
  }
  private startPollingCreateInstance(id: string, isStarting: boolean = false): void {
    const call = this.getInstanceByVmIdentifier(id);
    call
      .pipe(
        this.polling.pollUntill(45000, 45000, 40, (res: DemoInstance | null) => {
          if (res !== null && (res.available || res.powerState === PowerState.Online)) {
            this.demoUpdateSubject.next(res);
            return false;
          } else if (res !== null && res.powerState === PowerState.Deleted) {
            this.demoRemovedSubject.next(res.id);
            this.limitService.decreaseCurrentUsage();
            return false;
          }

          return true;
        })
      )
      .subscribe({
        error: () => {
          this.demoRemovedSubject.next(id);
          this.limitService.decreaseCurrentUsage();
        }
      });
  }
}
